import { RestQueryContext } from '@/providers/restQuery.provider'

import { type ChangeEvent, useCallback, useContext, useEffect, useRef } from 'react'

import type { AxiosError } from 'axios'
import { useAtom } from 'jotai'

import {
  errorSimulatorEnabledAtom,
  errorSimulatorErrorStatusAtom,
  errorSimulatorUrlPatternAtom,
} from './errorSimulator.store'

/**
 * Common HTTP error status codes available for simulation.
 */
export const ERROR_CODES = [
  { status: 400, label: '400 — Bad Request' },
  { status: 401, label: '401 — Unauthorized' },
  { status: 403, label: '403 — Forbidden' },
  { status: 404, label: '404 — Not Found' },
  { status: 409, label: '409 — Conflict' },
  { status: 422, label: '422 — Unprocessable Entity' },
  { status: 429, label: '429 — Too Many Requests' },
  { status: 500, label: '500 — Internal Server Error' },
  { status: 502, label: '502 — Bad Gateway' },
  { status: 503, label: '503 — Service Unavailable' },
  { status: 504, label: '504 — Gateway Timeout' },
] as const

export type ErrorStatus = (typeof ERROR_CODES)[number]['status']

/** Pre-formatted Select options for the error status dropdown. */
export const ERROR_CODE_OPTIONS = ERROR_CODES.map(({ status, label }) => ({
  value: String(status),
  label,
}))

type UseAPIErrorSimulatorOptions = {
  simulateError?: boolean
  onToggle?: (checked: boolean) => void
}

/**
 * Hook encapsulating the UI state management for APIErrorSimulator.
 *
 * Reads and writes to Jotai atoms (enabled, urlPattern, errorStatus).
 * The actual Axios interceptor is managed by `useAPIErrorSimulatorEffect`,
 * which must be called outside the Dialog to survive open/close cycles.
 *
 * Supports both controlled mode (via simulateError + onToggle) and uncontrolled mode.
 *
 * @param simulateError - Controlled: whether error simulation is active (optional)
 * @param onToggle - Controlled: callback when the toggle changes (optional)
 *
 * @returns simulateError - Whether error simulation is currently active
 * @returns urlPattern - Current URL pattern string
 * @returns errorStatus - Current error status code (as string, for Select compatibility)
 * @returns errorCodeOptions - Pre-formatted options for the Select component
 * @returns handleToggle - Checkbox change handler
 * @returns handleUrlPatternChange - Input change handler for the URL pattern
 * @returns handleStatusChange - Select change handler for the error status code
 *
 * @example
 * ```ts
 * // In APIErrorSimulator UI component (inside Dialog):
 * const api = useAPIErrorSimulator({})
 * ```
 */
export const useAPIErrorSimulator = ({
  simulateError: controlledSimulateError,
  onToggle,
}: UseAPIErrorSimulatorOptions) => {
  const isControlled = controlledSimulateError !== undefined
  const [internalSimulateError, setInternalSimulateError] = useAtom(errorSimulatorEnabledAtom)
  const [urlPattern, setUrlPattern] = useAtom(errorSimulatorUrlPatternAtom)
  const [errorStatusNumber, setErrorStatusNumber] = useAtom(errorSimulatorErrorStatusAtom)

  const simulateError = isControlled ? controlledSimulateError : internalSimulateError

  const handleToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const checked = e.target.checked
      if (isControlled) {
        onToggle?.(checked)
      } else {
        setInternalSimulateError(checked)
      }
    },
    [isControlled, onToggle, setInternalSimulateError],
  )

  const handleUrlPatternChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      setUrlPattern(e.target.value)
    },
    [setUrlPattern],
  )

  const handleStatusChange = useCallback(
    (value: string): void => {
      setErrorStatusNumber(Number(value))
    },
    [setErrorStatusNumber],
  )

  return {
    simulateError,
    urlPattern,
    errorStatus: String(errorStatusNumber),
    errorCodeOptions: ERROR_CODE_OPTIONS,
    handleToggle,
    handleUrlPatternChange,
    handleStatusChange,
  }
}

/**
 * Hook that registers and ejects the Axios response interceptor based on the
 * error-simulator Jotai atoms.
 *
 * **Must be called OUTSIDE the Dialog** (e.g. directly in TestingPanel) so the
 * interceptor remains active even when the Dialog is closed or when the user
 * navigates away from the "Error Simulator" tab inside the Dialog.
 *
 * Reads from `errorSimulatorEnabledAtom`, `errorSimulatorUrlPatternAtom`, and
 * `errorSimulatorErrorStatusAtom`. The UI component (`APIErrorSimulator`) only
 * writes to those atoms — this hook does the actual interception work.
 *
 * @example
 * ```tsx
 * // In TestingPanel, outside <Dialog>:
 * export const TestingPanel: FC = () => {
 *   useAPIErrorSimulatorEffect()
 *   return (
 *     <>
 *       <FloatingButton />
 *       <Dialog>...</Dialog>
 *     </>
 *   )
 * }
 * ```
 */
export const useAPIErrorSimulatorEffect = (): void => {
  const [simulateError] = useAtom(errorSimulatorEnabledAtom)
  const [urlPattern] = useAtom(errorSimulatorUrlPatternAtom)
  const [errorStatusNumber] = useAtom(errorSimulatorErrorStatusAtom)

  const apiContext = useContext(RestQueryContext)
  const interceptorIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (!apiContext?.axiosInstance) {
      // eslint-disable-next-line no-console
      console.warn('[APIErrorSimulator] Axios instance not found in context')
      return
    }
    const errorLabel =
      ERROR_CODES.find(c => c.status === errorStatusNumber)?.label ?? String(errorStatusNumber)

    const { axiosInstance } = apiContext

    // Eject any existing interceptor before potentially re-registering
    if (interceptorIdRef.current !== null) {
      axiosInstance.interceptors.response.eject(interceptorIdRef.current)
      interceptorIdRef.current = null
    }

    if (simulateError) {
      interceptorIdRef.current = axiosInstance.interceptors.response.use(
        response => {
          const url = response.config?.url || ''
          const matches = url.includes(urlPattern)

          if (matches) {
            // eslint-disable-next-line no-console
            console.log(
              `[APIErrorSimulator] Intercepted request to ${url}, returning ${errorStatusNumber}`,
            )

            const simulatedError: AxiosError = {
              response: {
                data: { error: errorLabel },
                status: errorStatusNumber,
                statusText: 'Error',
                headers: {},
                config: response.config,
              },
              isAxiosError: true,
              toJSON: () => ({}),
              name: 'AxiosError',
              message: errorLabel,
              config: response.config,
            } as AxiosError

            return Promise.reject(simulatedError)
          }

          return response
        },
        error => Promise.reject(error),
      )

      // eslint-disable-next-line no-console
      console.log('[APIErrorSimulator] Error simulation enabled')
    } else {
      // eslint-disable-next-line no-console
      console.log('[APIErrorSimulator] Error simulation disabled')
    }

    return () => {
      if (interceptorIdRef.current !== null && apiContext?.axiosInstance) {
        apiContext.axiosInstance.interceptors.response.eject(interceptorIdRef.current)
        interceptorIdRef.current = null
      }
    }
  }, [simulateError, apiContext, urlPattern, errorStatusNumber])
}
